"use client";

import { Instagram, Mail, Music2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="space-y-3">
        {[0, 1, 2, 3, 4].map(i => <li key={i} data-index={i} className="h-5 w-32 bg-muted/40 animate-pulse" />)}
      </ul>;
  }
  return <ul className="space-y-3 font-default text-base text-foreground">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="group">
          <Link to={item.href || "#"} className="relative inline-block text-foreground transition-colors">
            <span className="relative">
              {item.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[240ms] ease-out group-hover:w-full" />
            </span>
          </Link>
        </li>)}
    </ul>;
}
function MarginaliaRule({
  className = ""
}: {
  className?: string;
}) {
  return <div className={`flex items-center ${className}`}>
      <span className="h-px flex-1 bg-border" />
      <span className="ml-2 inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
    </div>;
}
function LibraryStamp() {
  return <div className="relative inline-flex items-center justify-center" style={{
    transform: "rotate(-7deg)"
  }}>
      <svg viewBox="0 0 120 120" className="h-28 w-28 text-primary/60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <path id="stamp-circle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
        </defs>
        <circle cx="60" cy="60" r="52" stroke="currentColor" strokeWidth="1.25" />
        <circle cx="60" cy="60" r="44" stroke="currentColor" strokeWidth="1" />
        <text className="font-mono" fill="currentColor" style={{
        fontSize: "8px",
        letterSpacing: "0.18em"
      }}>
          <textPath href="#stamp-circle" startOffset="0">
            VACAVILLE · CA · EST. 2024 · VACAVILLE · CA ·
          </textPath>
        </text>
        <g stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" fill="none">
          <circle cx="60" cy="60" r="10" />
          <line x1="60" y1="50" x2="60" y2="70" />
          <line x1="50" y1="60" x2="70" y2="60" />
          <line x1="53" y1="53" x2="67" y2="67" />
          <line x1="67" y1="53" x2="53" y2="67" />
        </g>
      </svg>
    </div>;
}
export default function BaseOperationsFooter() {
  const eyebrowMono = "font-mono uppercase text-[0.7rem] tracking-[0.18em] text-primary";
  return <section data-section-id="1179" id="base-operations-footer" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28">
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(circle at 20% 30%, hsl(215 90% 55%) 0.5px, transparent 1px), radial-gradient(circle at 70% 60%, hsl(215 90% 55%) 0.5px, transparent 1px), radial-gradient(circle at 40% 80%, hsl(215 90% 55%) 0.5px, transparent 1px)",
      backgroundSize: "120px 120px, 200px 200px, 160px 160px"
    }} aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-[1320px] px-6 md:px-10 lg:px-16">
        <div className="mb-12 flex items-center justify-between">
          
          
        </div>

        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-10 w-auto" />
            </Link>

            <p className="mt-6 font-default text-lg leading-relaxed text-foreground/90 max-w-md">Veteran-owned AI automation & design for all businesses, small or large. We put your buisiness on autopilot.</p>

            <MarginaliaRule className="mt-6 max-w-[180px]" />

            <address className="mt-6 not-italic font-mono text-[0.8125rem] leading-relaxed tracking-[0.08em] text-secondary">
              <div className="uppercase">38.3566° N · 121.9877° W</div>
              <div className="mt-1">VACAVILLE, CA</div>
              <div className="mt-3">
                
              </div>
              <div>
                <Link to="mailto:ops@aiguyz.com" className="text-secondary hover:text-primary transition-colors">thaaiguyz@gmail.com</Link>
              </div>
            </address>
          </div>

          <div className="lg:col-span-3">
            <div className={eyebrowMono}>PAGES</div>
            <MarginaliaRule className="mt-3 mb-6 max-w-[80px]" />
            <MenuProvider menu_id="45">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          <div className="lg:col-span-4">
            <div className={eyebrowMono}>§ CORRESPONDENCE</div>
            <MarginaliaRule className="mt-3 mb-6 max-w-[80px]" />
            <ul className="space-y-4 font-default text-base text-foreground">
              {[{
              icon: Instagram,
              label: "@aiguyz",
              to: "https://instagram.com"
            }, {
              icon: Music2,
              label: "@aiguyz",
              to: "https://tiktok.com"
            }, {
              icon: Mail,
              label: "Write to us",
              to: "mailto:ops@aiguyz.com"
            }].map((item, i) => {
              const Icon = item.icon;
              return <li key={item.label} data-index={i} className="group">
                    <Link to={item.to} newTab={item.to.startsWith("http")} className="flex items-center gap-3 text-foreground">
                      <Icon className="h-4 w-4 text-secondary group-hover:text-primary transition-colors" strokeWidth={1.25} />
                      <span className="relative">
                        {item.label}
                        <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-[240ms] ease-out group-hover:w-full" />
                      </span>
                    </Link>
                  </li>;
            })}
            </ul>

            <div className="mt-8">
              <Button asChild className="bg-primary text-primary-foreground font-mono uppercase tracking-[0.16em] text-xs rounded-md px-5 py-3 h-auto hover:bg-primary/90">
                
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-16 flex items-center">
          <span className="h-px flex-1 bg-border" />
          <span className="ml-2 inline-block h-1.5 w-1.5 rounded-lg bg-primary" />
        </div>

        <div className="mt-10 grid grid-cols-1 items-center gap-8 md:grid-cols-3">
          <div className="font-mono uppercase text-[0.75rem] tracking-[0.18em] text-secondary md:text-left text-center">© 2026 AI GUYZ OPERATIONS</div>

          <div className="flex justify-center">
            <LibraryStamp />
          </div>

          <div className="font-mono uppercase text-[0.75rem] tracking-[0.18em] text-secondary md:text-right text-center leading-relaxed">
            PRECISION AI
            <br />
            DEPLOYED NATIONWIDE
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center">
          <span className="font-default text-sm text-secondary">— INNOVATE AND CREATE —</span>
        </div>
      </div>
    </section>;
}